import { logError } from "@/lib/error-handling";
import type { CreateAdHocPrepInput } from "@/schemas";
import { apiClient } from "@/services/api";
import type {
  AccountPrepResponseDto,
  MeetingPrepDto,
  PrepSource,
  UpdatePrepPayload,
} from "@/types";

/**
 * The editable prep for a scheduled meeting, with its talking points ordered by
 * position. Never 404s for a meeting the caller can see — the prep is created on
 * first access if the calendar event synced before preps existed.
 */
export async function fetchPrepForActivity(
  activityId: string
): Promise<MeetingPrepDto> {
  try {
    return await apiClient<MeetingPrepDto>(
      `/api/activities/${activityId}/prep`,
      { method: "GET", cache: "no-store" }
    );
  } catch (error) {
    logError(error, "fetchPrepForActivity");
    throw error;
  }
}

/** A prep by its own id — the read path for ad hoc preps (no activity). */
export async function fetchPrepById(prepId: string): Promise<MeetingPrepDto> {
  try {
    return await apiClient<MeetingPrepDto>(`/api/preps/${prepId}`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchPrepById");
    throw error;
  }
}

/** The account's active ad hoc prep, or null — drives the Start Prep button. */
export async function fetchAccountPrep(
  accountId: string
): Promise<AccountPrepResponseDto> {
  try {
    return await apiClient<AccountPrepResponseDto>(
      `/api/accounts/${accountId}/prep`,
      { method: "GET", cache: "no-store" }
    );
  } catch (error) {
    logError(error, "fetchAccountPrep");
    throw error;
  }
}

/** Opens the account's active ad hoc prep, creating one when none exists. */
export async function createAdHocPrep(
  accountId: string,
  payload: CreateAdHocPrepInput
): Promise<MeetingPrepDto> {
  try {
    return await apiClient<MeetingPrepDto>(`/api/accounts/${accountId}/prep`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    logError(error, "createAdHocPrep");
    throw error;
  }
}

/** Dispatches the read to the right endpoint for the prep's source. */
export function fetchPrepForSource(
  source: PrepSource
): Promise<MeetingPrepDto> {
  return source.kind === "activity"
    ? fetchPrepForActivity(source.activityId)
    : fetchPrepById(source.prepId);
}

/** Goal edits and the save actions that drive the prep status machine. */
export async function updatePrep(
  prepId: string,
  payload: UpdatePrepPayload
): Promise<MeetingPrepDto> {
  try {
    return await apiClient<MeetingPrepDto>(`/api/preps/${prepId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    logError(error, "updatePrep");
    throw error;
  }
}

/** Marks the meeting as having taken place; the prep is frozen from here on. */
export async function completeCall(prepId: string): Promise<MeetingPrepDto> {
  try {
    return await apiClient<MeetingPrepDto>(
      `/api/preps/${prepId}/complete-call`,
      { method: "POST" }
    );
  } catch (error) {
    logError(error, "completeCall");
    throw error;
  }
}
